import { useEffect, useState } from "react"
import { DateTime } from "luxon"
import { ReadMonitorRunResultParams, useReadMonitorRunResultGroupedByMonitorIdQuery } from "services/monitorRunResult"
import { compareLuxonDates } from "shared/lib/compareLuxonDates"
import { ChartPaginationModel } from "../../presentationals/ChartPagination/ChartPagination"
import { Box, SxProps } from "@mui/material"
import { ChartSupply } from "../../presentationals/ChartSupply"
import { ChartDatePanel } from "../../presentationals/ChartDatePanel"
import { ButtonStyle, ChartDateMode, DateTimePeriod } from "../../presentationals/ChartDatePanel/ChartDatePanel"
import { median } from "shared/lib/median"
import { useReadMonitorByIdQuery } from "services/monitor"

interface ChartSupplyContainerProps {
    monitorId?: number
}

export const ChartSupplyContainer = (props: ChartSupplyContainerProps): JSX.Element => {
    const {
        monitorId
    } = props
    const [chartDateMode, setChartDateMode] = useState<ChartDateMode | undefined>(undefined)
    const [readMonitorRunResultByMonitorIdQueryParams, setReadMonitorRunResultByMonitorIdQueryParams] = useState<ReadMonitorRunResultParams>({
        monitorId: monitorId,
    })

    const [readMonitorRunResultByMonitorIdQueryOptions, setReadMonitorRunResultByMonitorIdQueryOptions] = useState<any>({
        skip: monitorId === undefined,
        refetchOnMountOrArgChange: true,
        pollingInterval: 5000,
    })

    const {
        data: readMonitorRunResultGroupedByMonitorIdData,
        isLoading: readMonitorRunResultGroupedByMonitorIdsLoading,
        isFetching: readMonitorRunResultGroupedByMonitorIdIsFetching,
    } = useReadMonitorRunResultGroupedByMonitorIdQuery(
        readMonitorRunResultByMonitorIdQueryParams,
        readMonitorRunResultByMonitorIdQueryOptions
    );

    const [chartDateTimePeriod, setChartDateTimePeriod] = useState<DateTimePeriod | undefined>(undefined)
    const [data, setData] = useState<any>()
    const [toSupplyGrow, setToSupplyGrow] = useState<number>()
    const [toSupplyGrowPercent, setToSupplyGrowPercent] = useState<number>()
    const [supplyMarketDirection, setSupplyMarketDirection] = useState<number | undefined>(undefined)
    const [supplyMarketDirectionGrow, setSupplyMarketDirectionGrow] = useState<number | undefined>(undefined)
    const [supplyMarketDirectionGrowPercent, setSupplyMarketDirectionGrowPercent] = useState<number | undefined>(undefined)
    const [chartSupplyData, setChartSupplyData] = useState<Object | undefined>()
    const [chartSupplyOptions, setChartSupplyOptions] = useState<any>({
        elements: {
            point: {
                radius: 5,
                hoverRadius: 7,
                hitRadius: 16
            }
        },
        responsive: true,
        scales: {
            y: {

                ticks: {
                    color: "#000000",
                    precision: 0,
                    beginAtZero: true
                }
            },
            x: {

                type: "timeseries",
                stacked: true,
                ticks: {
                    color: "#000000",
                    maxTicksLimit: 6
                },
                time: {
                    displayFormats: {
                        day: 'MM/dd/yyyy',
                        hour: 'MM/dd/yyyy',
                        minute: 'MM/dd/yyyy',
                    }
                },
                adapters: {
                    date: {
                        zone: 'utc'
                    }
                }
            }
        },
        plugins: {
            title: {
                display: true,
                text: 'Supply',
                font: {
                    size: 16
                }
            },
            legend: false,
            annotation: {
                annotations: {
                    label1: {
                        display: (obj: any, args: any, options: any) => {
                            return !obj.chart.data?.datasets.some((dataset: any) => dataset.data.length > 0)
                        },
                        type: 'label',
                        position: 'center',
                        backgroundColor: 'rgba(0,0,0, 0)',
                        content: ['No data available'],
                        font: {
                            size: 36,
                            weight: 600
                        }
                    }
                }
            }
        }
    })

    const [readMonitorByIdQueryParams, setReadMonitorByIdQueryParams] = useState<any>(undefined)
    const [readMonitorByIdOptions, setReadMonitorByIdOptions] = useState<any>(
        {
            skip: true
        }
    );

    const {
        data: readMonitorByIdQueryData,
        isLoading: readMonitorByIdQueryIsLoading
    } = useReadMonitorByIdQuery(readMonitorByIdQueryParams, readMonitorByIdOptions)

    useEffect(() => {
        setReadMonitorRunResultByMonitorIdQueryParams(
            {
                monitorId: monitorId,
            }
        )

        setReadMonitorRunResultByMonitorIdQueryOptions({
            skip: monitorId === undefined,
            refetchOnMountOrArgChange: true,
            pollingInterval: 5000,
        })

    }, [
        monitorId
    ])

    useEffect(() => {
        setReadMonitorByIdQueryParams((prevValue: any) => {
            return {
                ...prevValue,
                monitorId: monitorId
            }
        });

        setReadMonitorByIdOptions((prevValue: any) => {
            return {
                    ...prevValue,
                    skip: monitorId === undefined,
                }
        })
    }, [
        monitorId
    ])

    useEffect(() => {
        if (readMonitorByIdQueryData === undefined) {
            return
        }

        if (readMonitorByIdQueryData.customPeriods && readMonitorByIdQueryData.customPeriods.length > 0) {
            const startDate = DateTime.fromISO(readMonitorByIdQueryData.customPeriods[0].startDateTimeUtc, { zone: "UTC" }).startOf('day')
            const endDate = DateTime.fromISO(readMonitorByIdQueryData.customPeriods[0].endDateTimeUtc, { zone: "UTC" }).endOf('day')

            setChartDateTimePeriod((prevState: DateTimePeriod | undefined): any => {
                setChartDateTimePeriod({
                    from: startDate,
                    to: endDate
                })
            })

            setChartDateMode('Custom')

            return
        }

        setChartDateTimePeriod((prevState: DateTimePeriod | undefined): any => {
            setChartDateTimePeriod({
                from: DateTime.now().startOf('day'),
                to: DateTime.now().endOf('day').plus({ month: 1 })
            })
        })

        setChartDateMode('1M')
    },
        [
            readMonitorByIdQueryData
        ]
    )

    useEffect(() => {
        if (readMonitorRunResultGroupedByMonitorIdData === undefined) {
            setData(undefined)

            return
        }

        const transformedData: any[] = readMonitorRunResultGroupedByMonitorIdData.list
            .map((resultItem: any, index: number, list: any) => {
                const checkinUtc = DateTime.fromISO(resultItem?.checkinUtc, { setZone: true });

                return {
                    checkinUtc: checkinUtc,
                    supply: resultItem.supply
                }
            })
            .sort(compareLuxonDates)

        let trData = transformedData;

        if (chartDateTimePeriod !== undefined) {
            const fromDt = DateTime.fromObject({
                year: chartDateTimePeriod.from.year,
                month: chartDateTimePeriod.from.month,
                day: chartDateTimePeriod.from.day
            }, { zone: "UTC" })

            const toDt = DateTime.fromObject({
                year: chartDateTimePeriod.to.year,
                month: chartDateTimePeriod.to.month,
                day: chartDateTimePeriod.to.day,
                hour: 23,
                minute: 59,
                second: 59,
                millisecond: 999

            }, { zone: "UTC" })

            trData = transformedData.filter((row: any) => {
                return row.checkinUtc >= fromDt && row.checkinUtc <= toDt
            })
        }

        setData(trData)
    }, [readMonitorRunResultGroupedByMonitorIdData, chartDateTimePeriod]);

    useEffect(() => {
        function cleanState() {
            setToSupplyGrow(undefined)
            setToSupplyGrowPercent(undefined)
            setSupplyMarketDirection(undefined)
            setSupplyMarketDirectionGrow(undefined)
            setSupplyMarketDirectionGrowPercent(undefined)
            setChartSupplyData({
                datasets: [],
            })
        }

        function handleChartSupplyOptions() {
            setChartSupplyOptions((prevValue: any) => {
                const scales = {
                    ...prevValue.scales
                }

                let maxValue = Math.max.apply(null, data.map((o: any) => { return o.supply; }));
                let minValue = Math.min.apply(null, data.map((o: any) => { return o.supply; }));

                scales.y.min = minValue - 10;
                scales.y.max = maxValue + 10;

                return ({
                    ...prevValue,
                    scales
                });
            })
        }

        function handleStateAndDatasets() {
            if (data === undefined || data.length <= 0) {
                return
            }

            const datasets = []
            const startItem = data.find((row: any) => row.supply !== null)
            const endItem = data.findLast((row: any) => row.supply !== null)

            if (startItem === undefined || endItem === undefined) {
                return
            }

            const startSupply = startItem.supply
            const endSupply = endItem.supply
            const supplyGrow = endSupply - startSupply
            let percentGrow = undefined

            if (startSupply === 0) {
                percentGrow = (supplyGrow / 1) * 100
            } else {
                percentGrow = (supplyGrow / startSupply) * 100
            }

            if (supplyGrow >= 0) {
                percentGrow = Math.abs(percentGrow)
            }

            setToSupplyGrow(supplyGrow)
            setToSupplyGrowPercent(percentGrow)

            const arr = data
                .filter((row: any) => row.supply !== null)
                .map((o: any) => { return (o.supply) })

            let diffSupplyStartMedianSumm = undefined
            const medianResult = median(arr)

            if (medianResult !== undefined) {
                diffSupplyStartMedianSumm = medianResult - startSupply
                let percentSupplyMarketGrow: number | undefined = undefined

                if (startSupply === 0) {
                    percentSupplyMarketGrow = (diffSupplyStartMedianSumm / 1) * 100
                } else {
                    percentSupplyMarketGrow = (diffSupplyStartMedianSumm / startSupply) * 100
                }

                if (diffSupplyStartMedianSumm >= 0) {
                    percentSupplyMarketGrow = Math.abs(percentSupplyMarketGrow)
                }

                setSupplyMarketDirection(medianResult)
                setSupplyMarketDirectionGrow(diffSupplyStartMedianSumm)
                setSupplyMarketDirectionGrowPercent(percentSupplyMarketGrow)
                const borderColor = diffSupplyStartMedianSumm < 0 ? '#FE0000' : '#4caf50'

                datasets.push(
                    {
                        fill: 'start',
                        type: 'line' as const,
                        label: 'Market direction',
                        data: [
                            {
                                medianMarketDirectionGrow: startItem.supply,
                                checkinUtc: startItem.checkinUtc
                            },
                            {
                                medianMarketDirectionGrow: medianResult,
                                checkinUtc: endItem.checkinUtc
                            }
                        ],
                        parsing: {
                            yAxisKey: 'medianMarketDirectionGrow',
                            xAxisKey: 'checkinUtc',
                        },
                        borderColor: borderColor,
                        backgroundColor: () => (`rgba(254, 0, 0, 0)`)
                    }
                )
            }

            const negativeDirection = diffSupplyStartMedianSumm !== undefined && diffSupplyStartMedianSumm < 0
            const mainGrapBorderColor = negativeDirection ? '#FE0000' : '#4caf50'

            datasets.push({
                fill: 'start',
                type: 'line' as const,
                label: 'Supply',
                data: data,
                parsing: {
                    yAxisKey: 'supply',
                    xAxisKey: 'checkinUtc',
                },
                borderColor: mainGrapBorderColor,
                backgroundColor: function (context: any) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;

                    if (!chartArea) {
                        return;
                    }

                    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                    if (negativeDirection) {
                        gradient.addColorStop(0, 'rgba(254, 0, 0, 0.2)');
                        gradient.addColorStop(1, 'rgba(254, 0, 0, 0.05)');
                    } else {
                        gradient.addColorStop(0, 'rgba(76, 176, 80, 0.2)');
                        gradient.addColorStop(1, 'rgba(76, 176, 80, 0.05)');
                    }

                    return gradient;
                },
            })

            setChartSupplyData({
                datasets,
            })
        }

        cleanState()

        if (data === undefined) {
            return
        }

        handleChartSupplyOptions()
        handleStateAndDatasets()    
    }, [
        data
    ])

    const chartDatePanelButtonStyle: ButtonStyle = {
        color: {
            default: '#C0C0C0',
            hover: '#1976D3',
            selected: '#1976D3'
        },
        borderColor: {
            default: '#C0C0C0',
            hover: '#1976D3',
            selected: '#1976D3'
        }
    }

    const handleModeChange = (mode: ChartDateMode) => {
        setChartDateMode(mode)
    }

    const handleDateTimePeriodChange = (period: DateTimePeriod) => {
        setChartDateTimePeriod(period)
    }

    const namePropStyle = {
        fontWeight: '700',
        fontSize: '20px',
        margin: '0 0 10px 0',
    }

    const propertyListContainer = {
        padding: '20px 0px',
        gridTemplateColumns: 'max-content max-content max-content',
        display: 'grid',

    }

    const valuePropStyle = {
        margin: '0 0 10px 20px',
        fontSize: '20px',
        '&.CellValue__Root_isGrowing': {
            color: `#4CB050`
        },
        '&.CellValue__Root_isFalling': {
            color: `#FE0000`
        }
    }

    const renderValue = (value: any) => {
        if (value === undefined) {
            return (<Box></Box>)
        }

        let classes = `CellValue__Root_isUnchanged`
        let handledValue = `${value.toFixed(0)}`

        if (value < 0) {
            //classes = 'CellValue__Root_isFalling'
            handledValue = `-${Math.abs(value).toFixed(0)}`
        } else if (value > 0) {
            //classes = 'CellValue__Root_isGrowing'
            handledValue = `+${Math.abs(value).toFixed(0)}`
        }

        return (
            <Box className={classes}
                sx={valuePropStyle}>
                {handledValue}
            </Box>
        )
    }

    const renderValuePercent = (value: any) => {
        if (value === undefined) {
            return (<Box></Box>)
        }

        let classes = `CellValue__Root_isUnchanged`
        let handledValue = `${value.toFixed(2)}%`

        if (value < 0) {
            classes = 'CellValue__Root_isFalling'
            handledValue = `${value.toFixed(2)}%`
        } else if (value > 0) {
            classes = 'CellValue__Root_isGrowing'
            handledValue = `+${value.toFixed(2)}%`
        }

        return (
            <Box className={classes}
                sx={valuePropStyle}>
                {handledValue}
            </Box>
        )
    }

    const datesContainer: SxProps = {
        padding: '0 0',
        gridTemplateColumns: 'max-content 1fr',
        display: 'grid',
        fontSize: '20px'
    }

    const propertyTitle: SxProps = {
        fontWeight: '700',
        fontSize: '20px',
        margin: '0 10px 0 0'
    }

    const toDateFormat = (date: DateTime) => {
        return date.toFormat('M/dd/yyyy')
    }

    return (
        <Box>
            <Box sx={datesContainer}>
                <Box
                    sx={propertyTitle}
                >
                    Start:
                </Box>
                <Box>
                    {chartDateTimePeriod?.from ? toDateFormat(chartDateTimePeriod.from) : ''}
                </Box>
                <Box
                    sx={propertyTitle}
                >
                    End:
                </Box>
                <Box>
                    {chartDateTimePeriod?.to ? toDateFormat(chartDateTimePeriod.to) : ''}
                </Box>
            </Box>

            <ChartSupply
                data={chartSupplyData}
                options={chartSupplyOptions}
                loading={chartSupplyData === undefined}
            />
            <ChartDatePanel
                buttonStyle={chartDatePanelButtonStyle}
                mode={chartDateMode}
                onModeChange={handleModeChange}
                onDateTimePeriodChange={handleDateTimePeriodChange}
                dateTimePeriod={chartDateTimePeriod}
            />

            <Box sx={propertyListContainer}>
                <Box sx={namePropStyle}>
                    Supply:
                </Box>
                {
                    renderValue(toSupplyGrow)
                }
                {
                    renderValuePercent(toSupplyGrowPercent)
                }
                <Box sx={namePropStyle}>
                    Market direction:
                </Box>
                {
                    renderValue(supplyMarketDirectionGrow)
                }
                {
                    renderValuePercent(supplyMarketDirectionGrowPercent)
                }
            </Box>
        </Box>

    )
}